import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import '../styles/LoginModal.css';

const LoginModal = ({ isOpen, onClose, onSwitchToRegister, onSuccess }) => {
  const [view, setView] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot-password flow state
  const [resetStep, setResetStep] = useState('request'); // 'request' | 'reset'
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [loginNotice, setLoginNotice] = useState('');

  const { login } = useAuth();
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);

  // NOTE: Esc-to-close is intentionally disabled. This modal contains form
  // inputs (login / password reset), and an accidental Esc keypress would
  // discard everything the customer typed. They can close via the X button.

  // Focus trap and auto-focus first input
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [isOpen, view, resetStep]);

  const handleTrapFocus = useCallback((e) => {
    if (e.key !== 'Tab' || !modalRef.current) return;
    const focusableElements = modalRef.current.querySelectorAll(
      'input, button, textarea, select, a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length === 0) return;
    const firstEl = focusableElements[0];
    const lastEl = focusableElements[focusableElements.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      }
    } else {
      if (document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      onClose();
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setView('forgot');
    setResetStep('request');
    setResetError('');
    setResetMessage('');
    setResetCode('');
    setNewPassword('');
    setLoginNotice('');
  };

  const backToLogin = () => {
    setView('login');
    setError('');
  };

  // Step 1: request a reset code (always returns success to avoid email enumeration).
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');
    setResetLoading(true);
    try {
      await axios.post('/api/auth/request-password-reset', { email });
      setResetMessage('If that email is registered, we sent a 6-digit reset code. Enter it below.');
      setResetStep('reset');
    } catch (err) {
      setResetError(err.response?.data?.error || 'Could not send a reset code. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Step 2: submit code + new password.
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters.');
      return;
    }
    setResetLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { email, code: resetCode.trim(), newPassword });
      setView('login');
      setPassword('');
      setError('');
      // Surface a success note on the login view.
      setResetMessage('');
      setLoginNotice('Password reset. Please log in with your new password.');
    } catch (err) {
      setResetError(err.response?.data?.error || 'Reset failed. Check your code and try again.');
    } finally {
      setResetLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onKeyDown={handleTrapFocus}>
      <div className="modal-content" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="login-modal-title">
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>

        {view === 'login' ? (
          <>
            <h2 id="login-modal-title">Welcome Back</h2>
            <p className="modal-subtitle">Log in to access your business dashboard</p>

            {loginNotice && <div className="success-message" role="status">{loginNotice}</div>}
            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  ref={firstInputRef}
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Logging in...' : 'Log In'}
                </button>
              </div>

              <div className="form-footer">
                <p>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={onSwitchToRegister}
                  >
                    Sign up
                  </button>
                </p>
                <button type="button" className="link-button forgot-password" onClick={openForgot}>
                  Forgot password?
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 id="login-modal-title">Reset your password</h2>
            <p className="modal-subtitle">
              {resetStep === 'request'
                ? "Enter your email and we'll send you a reset code."
                : 'Enter the 6-digit code we emailed you and a new password.'}
            </p>

            {resetMessage && <div className="success-message" role="status">{resetMessage}</div>}
            {resetError && <div className="error-message">{resetError}</div>}

            {resetStep === 'request' ? (
              <form onSubmit={handleRequestReset}>
                <div className="form-group">
                  <label htmlFor="reset-email">Email Address</label>
                  <input
                    ref={firstInputRef}
                    type="email"
                    id="reset-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your@email.com"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={resetLoading}>
                    {resetLoading ? 'Sending...' : 'Send reset code'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label htmlFor="reset-code">Reset Code</label>
                  <input
                    ref={firstInputRef}
                    type="text"
                    id="reset-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    placeholder="6-digit code"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-password">New Password</label>
                  <input
                    type="password"
                    id="new-password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="At least 8 characters"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={resetLoading}>
                    {resetLoading ? 'Resetting...' : 'Reset password'}
                  </button>
                </div>
              </form>
            )}

            <div className="form-footer">
              <button type="button" className="link-button" onClick={backToLogin}>
                ← Back to log in
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
